// KyteProvider — thin class that wires the split modules together. The
// unified ShopAncillaries call delivers bags + seatMap + sportsEquipment +
// services in one envelope, so standalone seatmap / extra-bags hooks don't
// exist on Kyte (./ancillaries owns the step).

import Provider from '../base/provider'
import { validate, normalize, split_offers_from_response } from './normalize'
import { search, get_offer_details } from './upstream'
import { fetch_upsell } from './upsell'
import { get_bags, has_chargeable_bags } from './bags'
import { seats_are_mandatory } from './seats'
import { book_held, fetch_ancillaries, book_ancillaries, retrieve } from './ancillaries'
import { book, get_confirmation } from './checkout'

export default class KyteProvider extends Provider {
  static DEFAULT_CAPABILITIES = {
    flexibleBags: false,
    brandedFareUpsell: true,
    heldInfantSupported: true,
    heldInfantPairingAtSearch: false,
    ancillaryFlow: 'PREBOOK',
    // Kyte's LCC contract gathers bags / seats / sportsEquipment / services
    // against a held booking via /flights/shop/ancillaries; detail.vue
    // hides inline bags UI and routes through /ancillaries instead.
    ancillariesAsStep: true,
    supportsRepricing: true,
    supportsOnHold: true,
    supportsCancel: true,
    supportsModify: true,
    supports3DSAlwaysOn: true,
    needsExternalPayment: false,
    showsBaggageIncludedBadge: false,
    showsRefundableBadge: true,
    showsFareBrandBadge: true,
    // Kyte normalize stamps `includedCabinBags.quantity` from
    // baggageAllowance / fare description; Amadeus's cabin-bag info is
    // fare-text driven, so the capability stays off there.
    showIncludedCabinBags: true,
    priceMayChangeAtBook: false,
    // Kyte's bookFlight is one-shot (offer ids consume on first use); the FE
    // can't walk backwards through the wizard once /book lands. btn-back
    // renders as "Annulla" with a confirm modal whose only forward path is
    // /results (re-search).
    forwardsOnly: true,
  }

  constructor({ providerId = 'kyte', capabilities = {} } = {}) {
    super({ providerId, capabilities })
    this.version = 'kyte@v3.1.20'
  }

  validate(raw) {
    return validate(this, raw)
  }

  normalize(raw) {
    return normalize(this, raw)
  }

  search(payload) {
    return search(this, payload)
  }

  get_offer_details(flight_offer) {
    return get_offer_details(this, flight_offer)
  }

  // Pass-through for callers with a pre-fetched shop-flights response (tests,
  // offline fixtures) that want to skip the full get_search_results pipeline.
  split_offers_from_response(response) {
    return split_offers_from_response(this, response)
  }

  fetch_upsell(flight_offer) {
    return fetch_upsell(this, flight_offer)
  }

  get_bags(flight_offer, itinerary_index) {
    return get_bags(flight_offer, itinerary_index)
  }

  has_chargeable_bags(flight_offer, traveler_id) {
    return has_chargeable_bags(flight_offer, traveler_id)
  }

  seats_are_mandatory(flight_offer) {
    return seats_are_mandatory(flight_offer)
  }

  // ancillariesAsStep contract (./ancillaries)
  book_held(flight_offer, traveler_details, contact_details) {
    return book_held(this, flight_offer, traveler_details, contact_details)
  }

  fetch_ancillaries(flight_offer, booking_id) {
    return fetch_ancillaries(this, flight_offer, booking_id)
  }

  book_ancillaries(flight_offer, booking_id, selections) {
    return book_ancillaries(this, flight_offer, booking_id, selections)
  }

  retrieve(flight_offer, booking_id) {
    return retrieve(this, flight_offer, booking_id)
  }

  book(flight_offer, request) {
    return book(this, flight_offer, request)
  }

  get_confirmation(body) {
    return get_confirmation(this, body)
  }
}